import { itx } from "../../../../src/translationItx/itx";
import type { C1_FurnitureDefinition, C1_FurnitureRendererProps } from "../../types/C1_FurnitureTypes";
import { FurnitureCategory } from "../../types/C1_ModelTypes";
import { C1_FurnitureIcon, type C1_FurnitureIconKind } from "../C1_FurnitureIcon";
import { C1_FurnitureSvg } from "../C1_FurnitureSvg";

type StairShape = "straight" | "l" | "u";

const STANDARD_RISER_HEIGHT = 0.175;

const floorDifferenceProperty = {
  key: "floorDifference",
  get label() { return itx("c1.ui.Etagendifferenz_m", "Etagendifferenz (m)"); },
  get description() { return itx("c1.ui.Hohenunterschied_zwischen_den_verbundenen_Etagen", "Höhenunterschied zwischen den verbundenen Etagen."); },
  type: "number" as const,
  defaultValue: 3,
  min: 1,
  max: 6,
  step: 0.01,
  unit: "m",
};

const stairWidthProperty = {
  key: "stairWidth",
  get label() { return itx("c1.ui.Stufenbreite_m", "Stufenbreite (m)"); },
  get description() { return itx("c1.ui.Breite_eines_Treppenlaufs", "Breite eines Treppenlaufs."); },
  type: "number" as const,
  defaultValue: 1,
  min: 0.6,
  max: 2.5,
  step: 0.01,
  unit: "m",
};

const straightStairProperties = [
  floorDifferenceProperty,
  {
    key: "stairArrives",
    get label() { return itx("c1.ui.Treppe_kommt_an", "Treppe kommt an"); },
    get description() { return itx("c1.ui.Der_Teil_am_Ende_des_Pfeils_wird_durchgezogen_dargestellt", "Der Teil am Ende des Pfeils wird durchgezogen dargestellt."); },
    type: "boolean" as const,
    defaultValue: true,
  },
  {
    key: "stairLeaves",
    get label() { return itx("c1.ui.Treppe_geht_weg", "Treppe geht weg"); },
    get description() { return itx("c1.ui.Der_Teil_am_Anfang_des_Pfeils_wird_durchgezogen_dargestellt", "Der Teil am Anfang des Pfeils wird durchgezogen dargestellt."); },
    type: "boolean" as const,
    defaultValue: true,
  },
];
const lShapedStairProperties = [
  floorDifferenceProperty,
  {
    ...stairWidthProperty,
  },
  straightStairProperties[1],
  straightStairProperties[2],
];
const uShapedStairProperties = [
  floorDifferenceProperty,
  {
    ...stairWidthProperty,
  },
  straightStairProperties[1],
  straightStairProperties[2],
];

function lines(count: number, render: (index: number, ratio: number) => React.ReactNode) {
  return Array.from({ length: count }, (_, index) => render(index, (index + 1) / (count + 1)));
}

function StairArrow({
  startX,
  startY,
  endX,
  endY,
  stroke,
  strokeWidth,
  strokeDasharray,
}: {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  stroke: string;
  strokeWidth: number;
  strokeDasharray?: string;
}) {
  const deltaX = endX - startX;
  const deltaY = endY - startY;
  const length = Math.hypot(deltaX, deltaY);
  if (length <= 0) return null;

  const unitX = deltaX / length;
  const unitY = deltaY / length;
  const perpendicularX = -unitY;
  const perpendicularY = unitX;
  const arrowHeadLength = Math.min(8, length * 0.3);
  const arrowHeadWidth = Math.min(5, length * 0.2);
  const arrowHeadBaseX = endX - unitX * arrowHeadLength;
  const arrowHeadBaseY = endY - unitY * arrowHeadLength;

  return (
    <path
      d={`M${startX} ${startY}L${endX} ${endY}M${arrowHeadBaseX + perpendicularX * arrowHeadWidth} ${
        arrowHeadBaseY + perpendicularY * arrowHeadWidth
      }L${endX} ${endY}L${arrowHeadBaseX - perpendicularX * arrowHeadWidth} ${
        arrowHeadBaseY - perpendicularY * arrowHeadWidth
      }`}
      fill="none"
      stroke={stroke}
      strokeWidth={strokeWidth}
      strokeDasharray={strokeDasharray}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  );
}

function StairRenderer({
  shape,
  width,
  height,
  rotation,
  PX,
  strokeWidth,
  strokeColor,
  fillColor,
  floorDifference = 3,
  stairWidth = 1,
  stairArrives = true,
  stairLeaves = true,
  stairReversed = false,
}: C1_FurnitureRendererProps & { shape: StairShape }) {
  const stroke = strokeColor ?? "#000000";
  const fill = fillColor ?? "#ffffff";
  const lineWidth = strokeWidth ?? 2;
  const totalSteps = Math.max(3, Math.min(40, Math.round(Number(floorDifference) / STANDARD_RISER_HEIGHT) || 17));
  const widthInMeters = Math.max(width / PX, 0.01);
  const heightInMeters = Math.max(height / PX, 0.01);
  const resolvedStairWidth = Math.max(0.01, Number(stairWidth) || 1);
  const inset = Math.max(1, lineWidth);
  const left = inset;
  const top = inset;
  const right = Math.max(left, width - inset);
  const bottom = Math.max(top, height - inset);
  const innerWidth = right - left;
  const innerHeight = bottom - top;
  const requestedFlightWidth = resolvedStairWidth * PX;

  let content: React.ReactNode;
  if (shape === "straight") {
    const breakCenter = top + innerHeight / 2;
    const breakGap = Math.min(9, Math.max(4, innerHeight * 0.025));
    const breakProfile = [
      { x: left, offset: 0 },
      { x: left + innerWidth * 0.32, offset: 0 },
      { x: left + innerWidth * 0.43, offset: breakGap * 1.35 },
      { x: left + innerWidth * 0.57, offset: -breakGap * 1.35 },
      { x: left + innerWidth * 0.68, offset: 0 },
      { x: right, offset: 0 },
    ];
    const upperBreak = breakProfile.map(({ x, offset }) => ({ x, y: breakCenter - breakGap + offset }));
    const lowerBreak = breakProfile.map(({ x, offset }) => ({ x, y: breakCenter + breakGap + offset }));
    const points = (items: Array<{ x: number; y: number }>) => items.map(({ x, y }) => `${x},${y}`).join(" ");
    const dashedStroke = `${Math.max(4, lineWidth * 4)} ${Math.max(3, lineWidth * 3)}`;
    const upperStepLimit = Math.min(...upperBreak.map(({ y }) => y));
    const lowerStepLimit = Math.max(...lowerBreak.map(({ y }) => y));
    const arrowInset = Math.min(10, Math.max(3, (innerHeight / 2 - breakGap) * 0.12));
    content = (
      <>
        <polygon
          points={points([
            { x: left, y: top },
            { x: right, y: top },
            ...upperBreak.slice().reverse(),
          ])}
          fill={fill}
        />
        <polygon
          points={points([
            ...lowerBreak,
            { x: right, y: bottom },
            { x: left, y: bottom },
          ])}
          fill={fill}
        />
        <path
          d={`M${upperBreak[0].x} ${upperBreak[0].y}V${top}H${right}V${upperBreak[upperBreak.length - 1].y}`}
          stroke={stroke}
          strokeWidth={lineWidth * 2}
          strokeDasharray={stairArrives ? undefined : dashedStroke}
          fill="none"
          strokeLinejoin="round"
        />
        <polyline
          points={points(upperBreak)}
          fill="none"
          stroke={stroke}
          strokeWidth={lineWidth}
          strokeDasharray={stairArrives ? undefined : dashedStroke}
          strokeLinejoin="round"
        />
        <path
          d={`M${lowerBreak[0].x} ${lowerBreak[0].y}V${bottom}H${right}V${
            lowerBreak[lowerBreak.length - 1].y
          }`}
          stroke={stroke}
          strokeWidth={lineWidth * 2}
          strokeDasharray={stairLeaves ? undefined : dashedStroke}
          fill="none"
          strokeLinejoin="round"
        />
        <polyline
          points={points(lowerBreak)}
          fill="none"
          stroke={stroke}
          strokeWidth={lineWidth}
          strokeDasharray={stairLeaves ? undefined : dashedStroke}
          strokeLinejoin="round"
        />
        {lines(totalSteps, (index, ratio) => {
          const y = top + ratio * innerHeight;
          const belongsToEnd = y < upperStepLimit;
          const belongsToStart = y > lowerStepLimit;
          if (!belongsToEnd && !belongsToStart) return null;
          const isSolid = belongsToEnd ? stairArrives : stairLeaves;
          return (
            <line
              key={index}
              x1={left}
              y1={y}
              x2={right}
              y2={y}
              stroke={stroke}
              strokeWidth={lineWidth}
              strokeDasharray={isSolid ? undefined : dashedStroke}
            />
          );
        })}
        <StairArrow
          startX={width / 2}
          startY={bottom - arrowInset}
          endX={width / 2}
          endY={breakCenter + breakGap + arrowInset}
          stroke={stroke}
          strokeWidth={lineWidth}
          strokeDasharray={stairLeaves ? undefined : dashedStroke}
        />
        <StairArrow
          startX={width / 2}
          startY={breakCenter - breakGap - arrowInset}
          endX={width / 2}
          endY={top + arrowInset}
          stroke={stroke}
          strokeWidth={lineWidth}
          strokeDasharray={stairArrives ? undefined : dashedStroke}
        />
      </>
    );
  } else if (shape === "l") {
    const firstFlightLength = Math.max(0.01, heightInMeters - resolvedStairWidth);
    const secondFlightLength = Math.max(0.01, widthInMeters - resolvedStairWidth);
    const firstFlightSteps = Math.max(
      1,
      Math.min(totalSteps - 1, Math.round(totalSteps * (firstFlightLength / (firstFlightLength + secondFlightLength)))),
    );
    const secondFlightSteps = totalSteps - firstFlightSteps;
    const flightWidth = Math.min(requestedFlightWidth, innerWidth, innerHeight);
    const landingBottom = top + flightWidth;
    const firstFlightPixelLength = bottom - landingBottom;
    const secondFlightPixelLength = right - left - flightWidth;
    const halfTravelLength = (firstFlightLength + secondFlightLength) / 2;
    const breakOnFirstFlight = halfTravelLength <= firstFlightLength;
    const breakGap = Math.min(9, Math.max(4, Math.min(firstFlightPixelLength, secondFlightPixelLength) * 0.025));
    const dashedStroke = `${Math.max(4, lineWidth * 4)} ${Math.max(3, lineWidth * 3)}`;
    const points = (items: Array<{ x: number; y: number }>) => items.map(({ x, y }) => `${x},${y}`).join(" ");
    const firstArrowInset = Math.min(10, Math.max(3, firstFlightPixelLength * 0.12));
    const secondArrowInset = Math.min(10, Math.max(3, secondFlightPixelLength * 0.12));

    if (breakOnFirstFlight) {
      const breakCenter = bottom - (halfTravelLength / firstFlightLength) * firstFlightPixelLength;
      const profile = [
        { x: left, offset: 0 },
        { x: left + flightWidth * 0.32, offset: 0 },
        { x: left + flightWidth * 0.43, offset: breakGap * 1.35 },
        { x: left + flightWidth * 0.57, offset: -breakGap * 1.35 },
        { x: left + flightWidth * 0.68, offset: 0 },
        { x: left + flightWidth, offset: 0 },
      ];
      const upperBreak = profile.map(({ x, offset }) => ({ x, y: breakCenter - breakGap + offset }));
      const lowerBreak = profile.map(({ x, offset }) => ({ x, y: breakCenter + breakGap + offset }));
      const upperStepLimit = Math.min(...upperBreak.map(({ y }) => y));
      const lowerStepLimit = Math.max(...lowerBreak.map(({ y }) => y));
      const splitArrowInset = Math.min(firstArrowInset, Math.max(2, firstFlightPixelLength * 0.08));
      content = (
        <>
          <polygon
            points={points([...lowerBreak, { x: left + flightWidth, y: bottom }, { x: left, y: bottom }])}
            fill={fill}
          />
          <polygon
            points={points([
              { x: left, y: top },
              { x: right, y: top },
              { x: right, y: landingBottom },
              { x: left + flightWidth, y: landingBottom },
              ...upperBreak.slice().reverse(),
            ])}
            fill={fill}
          />
          <path
            d={`M${lowerBreak[0].x} ${lowerBreak[0].y}V${bottom}H${left + flightWidth}V${
              lowerBreak[lowerBreak.length - 1].y
            }`}
            fill="none"
            stroke={stroke}
            strokeWidth={lineWidth * 2}
            strokeDasharray={stairLeaves ? undefined : dashedStroke}
          />
          <polyline
            points={points(lowerBreak)}
            fill="none"
            stroke={stroke}
            strokeWidth={lineWidth}
            strokeDasharray={stairLeaves ? undefined : dashedStroke}
          />
          <path
            d={`M${upperBreak[0].x} ${upperBreak[0].y}V${top}H${right}V${landingBottom}H${
              left + flightWidth
            }V${upperBreak[upperBreak.length - 1].y}`}
            fill="none"
            stroke={stroke}
            strokeWidth={lineWidth * 2}
            strokeDasharray={stairArrives ? undefined : dashedStroke}
          />
          <polyline
            points={points(upperBreak)}
            fill="none"
            stroke={stroke}
            strokeWidth={lineWidth}
            strokeDasharray={stairArrives ? undefined : dashedStroke}
          />
          {lines(firstFlightSteps, (index, ratio) => {
            const y = landingBottom + ratio * firstFlightPixelLength;
            const belongsToEnd = y < upperStepLimit;
            const belongsToStart = y > lowerStepLimit;
            if (!belongsToStart && !belongsToEnd) return null;
            return (
              <line
                key={`a_${index}`}
                x1={left}
                y1={y}
                x2={left + flightWidth}
                y2={y}
                stroke={stroke}
                strokeWidth={lineWidth}
                strokeDasharray={(belongsToStart ? stairLeaves : stairArrives) ? undefined : dashedStroke}
              />
            );
          })}
          {lines(secondFlightSteps, (index, ratio) => (
            <line
              key={`b_${index}`}
              x1={left + flightWidth + ratio * secondFlightPixelLength}
              y1={top}
              x2={left + flightWidth + ratio * secondFlightPixelLength}
              y2={landingBottom}
              stroke={stroke}
              strokeWidth={lineWidth}
              strokeDasharray={stairArrives ? undefined : dashedStroke}
            />
          ))}
          <StairArrow
            startX={left + flightWidth / 2}
            startY={bottom - splitArrowInset}
            endX={left + flightWidth / 2}
            endY={breakCenter + breakGap + splitArrowInset}
            stroke={stroke}
            strokeWidth={lineWidth}
            strokeDasharray={stairLeaves ? undefined : dashedStroke}
          />
          <StairArrow
            startX={left + flightWidth / 2}
            startY={breakCenter - breakGap - splitArrowInset}
            endX={left + flightWidth / 2}
            endY={landingBottom + splitArrowInset}
            stroke={stroke}
            strokeWidth={lineWidth}
            strokeDasharray={stairArrives ? undefined : dashedStroke}
          />
          <StairArrow
            startX={left + flightWidth + secondArrowInset}
            startY={top + flightWidth / 2}
            endX={right - secondArrowInset}
            endY={top + flightWidth / 2}
            stroke={stroke}
            strokeWidth={lineWidth}
            strokeDasharray={stairArrives ? undefined : dashedStroke}
          />
        </>
      );
    } else {
      const secondFlightProgress = (halfTravelLength - firstFlightLength) / secondFlightLength;
      const breakCenter = left + flightWidth + secondFlightProgress * secondFlightPixelLength;
      const profile = [
        { y: top, offset: 0 },
        { y: top + flightWidth * 0.32, offset: 0 },
        { y: top + flightWidth * 0.43, offset: breakGap * 1.35 },
        { y: top + flightWidth * 0.57, offset: -breakGap * 1.35 },
        { y: top + flightWidth * 0.68, offset: 0 },
        { y: landingBottom, offset: 0 },
      ];
      const leftBreak = profile.map(({ y, offset }) => ({ x: breakCenter - breakGap + offset, y }));
      const rightBreak = profile.map(({ y, offset }) => ({ x: breakCenter + breakGap + offset, y }));
      const leftStepLimit = Math.min(...leftBreak.map(({ x }) => x));
      const rightStepLimit = Math.max(...rightBreak.map(({ x }) => x));
      content = (
        <>
          <polygon
            points={points([
              { x: left, y: top },
              ...leftBreak,
              { x: left + flightWidth, y: landingBottom },
              { x: left + flightWidth, y: bottom },
              { x: left, y: bottom },
            ])}
            fill={fill}
          />
          <polygon points={points([...rightBreak, { x: right, y: landingBottom }, { x: right, y: top }])} fill={fill} />
          <path
            d={`M${leftBreak[0].x} ${top}H${left}V${bottom}H${left + flightWidth}V${landingBottom}H${
              leftBreak[leftBreak.length - 1].x
            }`}
            fill="none"
            stroke={stroke}
            strokeWidth={lineWidth * 2}
            strokeDasharray={stairLeaves ? undefined : dashedStroke}
          />
          <polyline
            points={points(leftBreak)}
            fill="none"
            stroke={stroke}
            strokeWidth={lineWidth}
            strokeDasharray={stairLeaves ? undefined : dashedStroke}
          />
          <path
            d={`M${rightBreak[0].x} ${top}H${right}V${landingBottom}H${rightBreak[rightBreak.length - 1].x}`}
            fill="none"
            stroke={stroke}
            strokeWidth={lineWidth * 2}
            strokeDasharray={stairArrives ? undefined : dashedStroke}
          />
          <polyline
            points={points(rightBreak)}
            fill="none"
            stroke={stroke}
            strokeWidth={lineWidth}
            strokeDasharray={stairArrives ? undefined : dashedStroke}
          />
          {lines(firstFlightSteps, (index, ratio) => (
            <line
              key={`a_${index}`}
              x1={left}
              y1={landingBottom + ratio * firstFlightPixelLength}
              x2={left + flightWidth}
              y2={landingBottom + ratio * firstFlightPixelLength}
              stroke={stroke}
              strokeWidth={lineWidth}
              strokeDasharray={stairLeaves ? undefined : dashedStroke}
            />
          ))}
          {lines(secondFlightSteps, (index, ratio) => {
            const x = left + flightWidth + ratio * secondFlightPixelLength;
            const belongsToStart = x < leftStepLimit;
            const belongsToEnd = x > rightStepLimit;
            if (!belongsToStart && !belongsToEnd) return null;
            return (
              <line
                key={`b_${index}`}
                x1={x}
                y1={top}
                x2={x}
                y2={landingBottom}
                stroke={stroke}
                strokeWidth={lineWidth}
                strokeDasharray={(belongsToStart ? stairLeaves : stairArrives) ? undefined : dashedStroke}
              />
            );
          })}
          <StairArrow
            startX={left + flightWidth / 2}
            startY={bottom - firstArrowInset}
            endX={left + flightWidth / 2}
            endY={landingBottom + firstArrowInset}
            stroke={stroke}
            strokeWidth={lineWidth}
            strokeDasharray={stairLeaves ? undefined : dashedStroke}
          />
          <StairArrow
            startX={left + flightWidth + secondArrowInset}
            startY={top + flightWidth / 2}
            endX={breakCenter - breakGap - secondArrowInset}
            endY={top + flightWidth / 2}
            stroke={stroke}
            strokeWidth={lineWidth}
            strokeDasharray={stairLeaves ? undefined : dashedStroke}
          />
          <StairArrow
            startX={breakCenter + breakGap + secondArrowInset}
            startY={top + flightWidth / 2}
            endX={right - secondArrowInset}
            endY={top + flightWidth / 2}
            stroke={stroke}
            strokeWidth={lineWidth}
            strokeDasharray={stairArrives ? undefined : dashedStroke}
          />
        </>
      );
    }
  } else {
    const sideFlightLength = Math.max(0.01, heightInMeters - resolvedStairWidth);
    const lowerFlightLength = Math.max(0, widthInMeters - 2 * resolvedStairWidth);
    const hasLowerFlight = widthInMeters > 2 * resolvedStairWidth + 0.5;

    let firstFlightSteps = Math.ceil(totalSteps / 2);
    let secondFlightSteps = totalSteps - firstFlightSteps;
    let lowerFlightSteps = 0;

    if (hasLowerFlight) {
      const totalFlightLength = 2 * sideFlightLength + lowerFlightLength;
      const idealLowerFlightSteps = totalSteps * (lowerFlightLength / totalFlightLength);
      const validLowerFlightSteps = Array.from({ length: totalSteps - 2 }, (_, index) => index + 1).filter(
        (steps) => (totalSteps - steps) % 2 === 0,
      );

      lowerFlightSteps = validLowerFlightSteps.reduce((best, steps) =>
        Math.abs(steps - idealLowerFlightSteps) < Math.abs(best - idealLowerFlightSteps) ? steps : best,
      );
      firstFlightSteps = (totalSteps - lowerFlightSteps) / 2;
      secondFlightSteps = firstFlightSteps;
    }

    const flightWidth = Math.min(requestedFlightWidth, innerWidth / 2, innerHeight);
    const rightFlightStart = right - flightWidth;
    const landingBottom = top + flightWidth;
    const lowerFlightStart = left + flightWidth;
    const lowerFlightEnd = rightFlightStart;
    const sideArrowInset = Math.min(12, (bottom - landingBottom) * 0.2);
    const breakCenter = (left + right) / 2;
    const breakGap = Math.min(9, Math.max(4, innerWidth * 0.025));
    const breakProfile = [
      { y: top, offset: 0 },
      { y: top + flightWidth * 0.32, offset: 0 },
      { y: top + flightWidth * 0.43, offset: breakGap * 1.35 },
      { y: top + flightWidth * 0.57, offset: -breakGap * 1.35 },
      { y: top + flightWidth * 0.68, offset: 0 },
      { y: landingBottom, offset: 0 },
    ];
    const leftBreak = breakProfile.map(({ y, offset }) => ({ x: breakCenter - breakGap + offset, y }));
    const rightBreak = breakProfile.map(({ y, offset }) => ({ x: breakCenter + breakGap + offset, y }));
    const points = (items: Array<{ x: number; y: number }>) => items.map(({ x, y }) => `${x},${y}`).join(" ");
    const dashedStroke = `${Math.max(4, lineWidth * 4)} ${Math.max(3, lineWidth * 3)}`;
    const leftStepLimit = Math.min(...leftBreak.map(({ x }) => x));
    const rightStepLimit = Math.max(...rightBreak.map(({ x }) => x));
    const middleArrowInset = Math.min(10, Math.max(3, (lowerFlightEnd - lowerFlightStart) * 0.12));
    content = (
      <>
        <polygon
          points={points([
            { x: left, y: top },
            ...leftBreak,
            { x: left + flightWidth, y: landingBottom },
            { x: left + flightWidth, y: bottom },
            { x: left, y: bottom },
          ])}
          fill={fill}
        />
        <polygon
          points={points([
            ...rightBreak,
            { x: rightFlightStart, y: landingBottom },
            { x: rightFlightStart, y: bottom },
            { x: right, y: bottom },
            { x: right, y: top },
          ])}
          fill={fill}
        />
        <path
          d={`M${leftBreak[0].x} ${top}H${left}V${bottom}H${left + flightWidth}V${landingBottom}H${
            leftBreak[leftBreak.length - 1].x
          }`}
          stroke={stroke}
          strokeWidth={lineWidth * 2}
          strokeDasharray={stairLeaves ? undefined : dashedStroke}
          fill="none"
          strokeLinejoin="round"
        />
        <polyline
          points={points(leftBreak)}
          fill="none"
          stroke={stroke}
          strokeWidth={lineWidth}
          strokeDasharray={stairLeaves ? undefined : dashedStroke}
          strokeLinejoin="round"
        />
        <path
          d={`M${rightBreak[0].x} ${top}H${right}V${bottom}H${rightFlightStart}V${landingBottom}H${
            rightBreak[rightBreak.length - 1].x
          }`}
          stroke={stroke}
          strokeWidth={lineWidth * 2}
          strokeDasharray={stairArrives ? undefined : dashedStroke}
          fill="none"
          strokeLinejoin="round"
        />
        <polyline
          points={points(rightBreak)}
          fill="none"
          stroke={stroke}
          strokeWidth={lineWidth}
          strokeDasharray={stairArrives ? undefined : dashedStroke}
          strokeLinejoin="round"
        />
        {lines(firstFlightSteps, (index, ratio) => (
          <line
            key={`a_${index}`}
            x1={left}
            y1={landingBottom + ratio * (bottom - landingBottom)}
            x2={left + flightWidth}
            y2={landingBottom + ratio * (bottom - landingBottom)}
            stroke={stroke}
            strokeWidth={lineWidth}
            strokeDasharray={stairLeaves ? undefined : dashedStroke}
          />
        ))}
        {lines(secondFlightSteps, (index, ratio) => (
          <line
            key={`b_${index}`}
            x1={rightFlightStart}
            y1={landingBottom + ratio * (bottom - landingBottom)}
            x2={right}
            y2={landingBottom + ratio * (bottom - landingBottom)}
            stroke={stroke}
            strokeWidth={lineWidth}
            strokeDasharray={stairArrives ? undefined : dashedStroke}
          />
        ))}
        {lines(lowerFlightSteps, (index, ratio) => {
          const x = lowerFlightStart + ratio * (lowerFlightEnd - lowerFlightStart);
          const belongsToStart = x < leftStepLimit;
          const belongsToEnd = x > rightStepLimit;
          if (!belongsToStart && !belongsToEnd) return null;
          return (
            <line
              key={`c_${index}`}
              x1={x}
              y1={top}
              x2={x}
              y2={landingBottom}
              stroke={stroke}
              strokeWidth={lineWidth}
              strokeDasharray={(belongsToStart ? stairLeaves : stairArrives) ? undefined : dashedStroke}
            />
          );
        })}
        <StairArrow
          startX={left + flightWidth / 2}
          startY={bottom - sideArrowInset}
          endX={left + flightWidth / 2}
          endY={landingBottom + sideArrowInset}
          stroke={stroke}
          strokeWidth={lineWidth}
          strokeDasharray={stairLeaves ? undefined : dashedStroke}
        />
        <StairArrow
          startX={right - flightWidth / 2}
          startY={landingBottom + sideArrowInset}
          endX={right - flightWidth / 2}
          endY={bottom - sideArrowInset}
          stroke={stroke}
          strokeWidth={lineWidth}
          strokeDasharray={stairArrives ? undefined : dashedStroke}
        />
        {lowerFlightSteps > 0 && breakCenter - breakGap - lowerFlightStart > middleArrowInset * 2 && (
          <>
            <StairArrow
              startX={lowerFlightStart + middleArrowInset}
              startY={top + flightWidth / 2}
              endX={breakCenter - breakGap - middleArrowInset}
              endY={top + flightWidth / 2}
              stroke={stroke}
              strokeWidth={lineWidth}
              strokeDasharray={stairLeaves ? undefined : dashedStroke}
            />
            <StairArrow
              startX={breakCenter + breakGap + middleArrowInset}
              startY={top + flightWidth / 2}
              endX={lowerFlightEnd - middleArrowInset}
              endY={top + flightWidth / 2}
              stroke={stroke}
              strokeWidth={lineWidth}
              strokeDasharray={stairArrives ? undefined : dashedStroke}
            />
          </>
        )}
      </>
    );
  }

  const transform = (shape === "straight") !== (stairReversed === true) ? `translate(${width} ${height}) scale(-1 -1)` : undefined;

  return (
    <C1_FurnitureSvg
      PX={PX}
      width={width}
      height={height}
      rotation={rotation}
      viewBoxWidth={width}
      viewBoxHeight={height}
      originXPercentage={0}
      originYPercentage={0}
    >
      <g transform={transform}>{content}</g>
    </C1_FurnitureSvg>
  );
}

function createStairDefinition(
  id: string,
  shape: StairShape,
  icon: C1_FurnitureIconKind,
  description: C1_FurnitureDefinition["description"],
  defaultWidth: number,
  defaultHeight: number,
  properties: C1_FurnitureDefinition["properties"],
): C1_FurnitureDefinition {
  return {
    id,
    icon: <C1_FurnitureIcon kind={icon} />,
    description,
    category: FurnitureCategory.Staircase,
    defaultWidth,
    defaultHeight,
    properties,
    Renderer: (props) => <StairRenderer {...props} shape={shape} />,
  };
}

export const StraightStair = createStairDefinition(
  "straightStair",
  "straight",
  "stairs-straight",
  { de: "Treppe", en: "Straight staircase" },
  1,
  4,
  straightStairProperties,
);

export const LShapedStair = createStairDefinition(
  "lShapedStair",
  "l",
  "stairs-l",
  { de: "Treppe L", en: "L-shaped staircase" },
  2.5,
  3,
  lShapedStairProperties,
);

export const UShapedStair = createStairDefinition(
  "uShapedStair",
  "u",
  "stairs-u",
  { de: "Treppe U", en: "U-shaped staircase" },
  2.5,
  4,
  uShapedStairProperties,
);
